import { useMemo } from 'react';

import { useFilters } from '@spotify/confidence-core-react';
import type { CollectionEntryFragment } from '@spotify/confidence-graphql';
import {
  OWNED_BY_ME_FILTER_ID,
  useCurrentUserAndGroupsFilter,
} from '@spotify/plugin-confidence-iam-react';

import {
  type CollectionEntryExtensionOptions,
  findCollectionEntryExtension,
  getExtensionDisplayName,
} from '../../extensions';
import { FAVORITES_FILTER } from '../FavoritesFilterToggle';
import { useFavorites } from '../FavoritesProvider';
import { RESOURCE_TYPE_FILTER_ID } from './collectionTypeFilter';

const getResourceName = (entry: CollectionEntryFragment): string => {
  const resource = entry.resource;
  return resource && 'name' in resource ? String(resource.name) : entry.name;
};

const getOwnerName = (entry: CollectionEntryFragment): string | undefined => {
  const resource = entry.resource;
  if (!resource || !('owner' in resource)) return undefined;
  const { owner } = resource;
  if (!owner || typeof owner !== 'object') return undefined;
  return 'name' in owner ? String(owner.name) : undefined;
};

export const useCollectionEntriesFilter = (
  entries: CollectionEntryFragment[] | undefined,
  extensions: CollectionEntryExtensionOptions[],
) => {
  const { searchQuery, activeFilters } = useFilters();
  const { entryNamesByResource } = useFavorites();
  const { identityNames } = useCurrentUserAndGroupsFilter({ field: 'owner' });

  const availableTypes = useMemo(() => {
    if (!entries) return [];
    const types = new Set<string>();
    for (const entry of entries) {
      const extension = findCollectionEntryExtension(extensions, entry);
      types.add(extension?.resourceType ?? 'Unknown');
    }
    return [...types].sort((a, b) => a.localeCompare(b));
  }, [entries, extensions]);

  const selectedTypes = useMemo(() => {
    const filter = activeFilters.find(f => f.id === RESOURCE_TYPE_FILTER_ID);
    if (!filter) return undefined;
    const value = filter.value;
    if (!Array.isArray(value) || value.length === 0) return undefined;
    const valid = value.filter(type => availableTypes.includes(type));
    return valid.length > 0 ? valid : undefined;
  }, [activeFilters, availableTypes]);

  const favoritesActive = activeFilters.some(f => f.id === FAVORITES_FILTER.id);
  const ownedByMeActive = activeFilters.some(
    f => f.id === OWNED_BY_ME_FILTER_ID,
  );

  const effectiveTypes = useMemo(() => {
    if (availableTypes.length === 1) return availableTypes;
    return selectedTypes ?? availableTypes;
  }, [availableTypes, selectedTypes]);

  const activeExtension = useMemo(() => {
    if (effectiveTypes.length !== 1) return undefined;
    return extensions.find(ext => ext.resourceType === effectiveTypes[0]);
  }, [extensions, effectiveTypes]);

  const filteredEntries = useMemo(() => {
    if (!entries) return [];

    let result = entries;

    if (selectedTypes) {
      result = result.filter(entry => {
        const ext = findCollectionEntryExtension(extensions, entry);
        const type = ext?.resourceType ?? 'Unknown';
        return selectedTypes.includes(type);
      });
    }

    if (favoritesActive) {
      result = result.filter(entry =>
        entryNamesByResource.has(getResourceName(entry)),
      );
    }

    if (ownedByMeActive) {
      result = result.filter(entry => {
        const ownerName = getOwnerName(entry);
        return ownerName ? identityNames.includes(ownerName) : false;
      });
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(entry => {
        const ext = findCollectionEntryExtension(extensions, entry);
        const name = entry.resource
          ? getExtensionDisplayName(ext, entry.resource, entry)
          : entry.name;
        return name.toLowerCase().includes(query);
      });
    }

    return result;
  }, [
    entries,
    selectedTypes,
    favoritesActive,
    ownedByMeActive,
    entryNamesByResource,
    identityNames,
    searchQuery,
    extensions,
  ]);

  const hasActiveFilters = Boolean(searchQuery) || activeFilters.length > 0;

  return {
    filteredEntries,
    availableTypes,
    activeExtension,
    hasActiveFilters,
  };
};
